import { createApi } from '@reduxjs/toolkit/query/react';
import { apiQuery } from 'core/rtkQuery';
import {
  CalculationMetaDataDto,
  PublishSlmBusinessEventRequest,
  AddStoreToRedisRequest,
  BuCodeAssignmentsDto,
  MhsWarehouseSyncDto,
  WhmRemovedAssignmentsDto,
} from 'apis/backendApi';
import { addQueryContext, WithQueryContext } from 'core/apiQueryContext';

const utilityApiDef = createApi({
  reducerPath: 'utilityApi',
  baseQuery: apiQuery(),
  refetchOnMountOrArgChange: 5,

  endpoints: builder => ({
    getCalculationsItemMetadata: builder.query<
      CalculationMetaDataDto,
      WithQueryContext<{ itemNo: string }>
    >({
      query: ({ buCode, itemNo }) => ({
        url: '/calculations/v1/{buCode}/metadata/{itemNo}',
        pathVariables: {
          buCode,
          itemNo,
        },
      }),
    }),

    postCalculationsTriggerItem: builder.mutation<
      void,
      WithQueryContext<{ itemNo: string; date?: string }>
    >({
      query: ({ buCode, itemNo, date }) => ({
        method: 'POST',
        url: '/calculations/v1/{buCode}/trigger/{itemNo}',
        pathVariables: {
          buCode,
          itemNo,
        },
        params: { date },
      }),
    }),

    getCalculationsStoreMetadata: builder.query<
      CalculationMetaDataDto,
      WithQueryContext<{}>
    >({
      query: ({ buCode }) => ({
        url: '/calculations/v1/{buCode}/metadata',
        pathVariables: {
          buCode,
        },
      }),
    }),

    postCalculationsTriggerStore: builder.mutation<
      void,
      WithQueryContext<{ date?: string }>
    >({
      query: ({ buCode, date }) => ({
        method: 'POST',
        url: '/calculations/v1/{buCode}/trigger',
        params: { date },
        pathVariables: {
          buCode,
        },
      }),
    }),

    postCalculationsTriggerSummary: builder.mutation<
      void,
      WithQueryContext<{}>
    >({
      query: ({ buCode }) => ({
        method: 'POST',
        url: '/calculations/v1/{buCode}/summary',
        pathVariables: {
          buCode,
        },
      }),
    }),

    postItemAssignmentMigration: builder.mutation<void, WithQueryContext<{}>>({
      query: ({ buCode }) => ({
        method: 'POST',
        url: '/items/v1/{buCode}/assignment-migration',
        params: { background: true },
        pathVariables: {
          buCode,
        },
      }),
    }),
    postBaseloadTrigger: builder.mutation<void, WithQueryContext<{}>>({
      query: ({ buCode }) => ({
        method: 'POST',
        url: '/store/v1/{buCode}/trigger-mhs-baseload',
        pathVariables: {
          buCode,
        },
      }),
    }),
    postPublishEvents: builder.mutation<
      void,
      WithQueryContext<{ dto: PublishSlmBusinessEventRequest }>
    >({
      query: ({ buCode, dto }) => ({
        method: 'POST',
        url: '/common/v1/{buCode}/publish-slm-business-event',
        pathVariables: {
          buCode,
        },
        body: dto,
      }),
    }),
    addStoreToRedis: builder.mutation<
      void,
      WithQueryContext<{ dto: AddStoreToRedisRequest }>
    >({
      query: ({ buCode, dto }) => ({
        method: 'POST',
        url: '/common/v1/{buCode}/add-store-to-redis',
        pathVariables: {
          buCode,
        },
        body: dto,
      }),
    }),
    getAllStoresInRedis: builder.query<
      BuCodeAssignmentsDto,
      WithQueryContext<{}>
    >({
      query: ({ buCode }) => ({
        method: 'GET',
        url: '/common/v1/{buCode}/get-all-stores-in-redis',
        pathVariables: {
          buCode,
        },
      }),
    }),
    getMhsWarehouseSync: builder.query<
      MhsWarehouseSyncDto,
      WithQueryContext<{}>
    >({
      query: ({ buCode }) => ({
        method: 'GET',
        url: '/common/v1/{buCode}/mhs-whm-sync',
        pathVariables: {
          buCode,
        },
      }),
    }),
    postMhsWarehouseSync: builder.mutation<void, WithQueryContext<{}>>({
      query: ({ buCode }) => ({
        method: 'POST',
        url: '/common/v1/{buCode}/mhs-whm-sync',
        pathVariables: {
          buCode,
        },
      }),
    }),
    getWhmRemovedAssignments: builder.query<
      WhmRemovedAssignmentsDto,
      WithQueryContext<{ days: number; itemNo?: string }>
    >({
      query: ({ buCode, days, itemNo }) => ({
        method: 'GET',
        url: '/common/v1/{buCode}/whm-removed-assignments',
        pathVariables: {
          buCode,
        },
        params: { days, itemNo },
      }),
    }),
  }),
});

const utilityApi = addQueryContext(utilityApiDef);

export const {
  useLazyGetCalculationsItemMetadataQuery,
  usePostCalculationsTriggerItemMutation,
  usePostCalculationsTriggerStoreMutation,
  usePostCalculationsTriggerSummaryMutation,
  useLazyGetCalculationsStoreMetadataQuery,
  usePostItemAssignmentMigrationMutation,
  usePostBaseloadTriggerMutation,
  usePostPublishEventsMutation,
  useAddStoreToRedisMutation,
  useLazyGetAllStoresInRedisQuery,
  useLazyGetMhsWarehouseSyncQuery,
  usePostMhsWarehouseSyncMutation,
  useLazyGetWhmRemovedAssignmentsQuery,
} = utilityApi;

export default utilityApi;
